import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { safeFormatDate } from '../lib/dateTime';
import {
  AlertCircle,
  CheckCircle2,
  Users,
  Calendar,
  ChevronDown,
  ChevronUp,
  User,
  Mail,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

/**
 * Component showing which team members haven't submitted their weeks yet.
 * Only visible for supervisor/admin roles.
 */
export function MissingSubmissionsPanel() {
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [weeksToCheck, setWeeksToCheck] = useState('1');
  const [expandedWeeks, setExpandedWeeks] = useState({});

  const fetchMissingSubmissions = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/approvals/missing?weeks=${weeksToCheck}`);
      setData(response.data);
      // Auto-expand weeks with missing submissions
      const expanded = {};
      response.data?.weeks?.forEach(w => {
        if (w.missing_count > 0) {
          expanded[w.week_start] = true;
        }
      });
      setExpandedWeeks(expanded);
    } catch (error) {
      console.error('Failed to fetch missing submissions:', error);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'supervisor' || user?.role === 'admin') {
      fetchMissingSubmissions();
    }
  }, [weeksToCheck, user?.role]);

  // Only show for supervisor/admin
  if (user?.role !== 'supervisor' && user?.role !== 'admin') {
    return null;
  }

  const toggleWeek = (weekStart) => {
    setExpandedWeeks(prev => ({
      ...prev,
      [weekStart]: !prev[weekStart]
    }));
  };

  if (isLoading) {
    return (
      <div className="card-glass p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-glass p-6 mb-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            data?.total_missing > 0 ? 'bg-amber-500/20' : 'bg-emerald-500/20'
          }`}>
            <Users className={`w-5 h-5 ${
              data?.total_missing > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'
            }`} />
          </div>
          <div>
            <h3 className="text-lg font-medium text-foreground">Ausstehende Einreichungen</h3>
            <p className="text-sm text-muted-foreground">
              {data?.total_missing > 0 
                ? `${data.total_missing} fehlende Wochen-Einreichungen` 
                : 'Alle Wochen eingereicht'}
            </p>
          </div>
        </div>
        
        {/* Weeks selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Zeitraum:</span>
          <Select value={weeksToCheck} onValueChange={setWeeksToCheck}>
            <SelectTrigger className="w-[140px] bg-muted/50 border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Letzte Woche</SelectItem>
              <SelectItem value="2">Letzte 2 Wochen</SelectItem>
              <SelectItem value="4">Letzte 4 Wochen</SelectItem>
              <SelectItem value="8">Letzte 8 Wochen</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      {data?.total_missing === 0 ? (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <span className="text-emerald-600 dark:text-emerald-400">
            Alle Teammitglieder haben ihre Wochen eingereicht
          </span>
        </div>
      ) : (
        <div className="space-y-3">
          {data?.weeks?.map((week) => (
            <div 
              key={week.week_start}
              className={`rounded-lg border ${
                week.missing_count > 0 
                  ? 'border-amber-500/20 bg-amber-500/5' 
                  : 'border-border bg-muted/50'
              }`}
            >
              {/* Week header - clickable */}
              <button
                onClick={() => toggleWeek(week.week_start)}
                className="w-full flex items-center justify-between p-3 hover:bg-accent transition-colors rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Calendar className={`w-4 h-4 ${
                    week.missing_count > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
                  }`} />
                  <span className="text-sm font-medium text-foreground">
                    KW {week.kw} ({safeFormatDate(week.week_start, 'd. MMM')} – {safeFormatDate(week.week_end, 'd. MMM yyyy')})
                  </span>
                  {week.missing_count > 0 && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400">
                      {week.missing_count} fehlend
                    </span>
                  )}
                  {week.missing_count === 0 && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-500/20 text-emerald-400">
                      Vollständig
                    </span>
                  )}
                </div>
                {week.missing_count > 0 && (
                  expandedWeeks[week.week_start] 
                    ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
              
              {/* Expanded member list */}
              <AnimatePresence>
                {expandedWeeks[week.week_start] && week.missing_count > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 pb-3 space-y-1">
                      {week.missing?.map((member) => (
                        <div 
                          key={member.user_id}
                          className="flex items-center gap-3 p-2 rounded bg-muted/50"
                        >
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-foreground flex-1">{member.name}</span>
                          {member.email && (
                            <a 
                              href={`mailto:${member.email}?subject=Woche ${week.kw} - Bitte Zeiten einreichen`}
                              className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-300"
                              title="E-Mail senden"
                            >
                              <Mail className="w-3 h-3" />
                              <span className="hidden sm:inline">Erinnern</span>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

export default MissingSubmissionsPanel;
